import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/Dialog";
import BrandSearchInput from "./BrandSearchInput";

// Generic type for form data
interface FormField {
  name: string;
  label: string;
  type: "text" | "select" | "brand-search";
  required?: boolean;
  options?: Array<{ value: string | number; label: string }>;
  initialValue?: string | number;
}

interface GearItemFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: Record<string, any>) => void;
  title: string;
  fields: FormField[];
  apiError: string;
  editingItem?: Record<string, any> | null;
}

function GearItemFormDialog({
  isOpen,
  onClose,
  onSave,
  title,
  fields,
  apiError,
  editingItem,
}: GearItemFormDialogProps) {
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Para controlar si los inputs deben reiniciarse
  const [shouldResetForm, setShouldResetForm] = useState(true);

  // Initialize form values when dialog opens or editing item changes
  useEffect(() => {
    if (isOpen && shouldResetForm) {
      setErrors({});

      const initialValues: Record<string, any> = {};

      // If we're editing a model, make sure to include kitebrandname
      if (
        editingItem &&
        "kitebrandid" in editingItem &&
        "kitebrandname" in editingItem
      ) {
        initialValues.kitebrandname = editingItem.kitebrandname;
      }

      fields.forEach((field) => {
        if (editingItem && field.name in editingItem) {
          initialValues[field.name] = editingItem[field.name];
        } else if (field.initialValue !== undefined) {
          initialValues[field.name] = field.initialValue;
        } else {
          initialValues[field.name] = field.type === "text" ? "" : "";
        }
      });

      setFormValues(initialValues);
      setShouldResetForm(false);
    }
  }, [isOpen, editingItem, fields, shouldResetForm]);

  useEffect(() => {
    if (!isOpen) {
      setShouldResetForm(true);
    }
  }, [isOpen]);

  // Handle form field changes
  const handleChange = (name: string, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user makes changes
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle brand selection with both ID and name
  const handleBrandChange = (brandId: number, brandName: string) => {
    setFormValues((prev) => ({
      ...prev,
      kitebrandid: brandId,
      kitebrandname: brandName,
    }));

    // Clear error for this field when user makes changes
    if (errors.kitebrandid) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.kitebrandid;
        return newErrors;
      });
    }
  };

  // Validate form before submission
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    fields.forEach((field) => {
      if (field.required) {
        const value = formValues[field.name];
        if (
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "") ||
          (field.type === "select" && value === "")
        ) {
          newErrors[field.name] = `${field.label} is required`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = () => {
    if (validateForm()) {
      onSave(formValues);
    }
  };

  // Render form fields
  const renderField = (field: FormField) => {
    const value =
      formValues[field.name] !== undefined ? formValues[field.name] : "";
    const error = errors[field.name];

    switch (field.type) {
      case "text":
        return (
          <div key={field.name} className="mb-4">
            <label
              htmlFor={field.name}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              id={field.name}
              value={value}
              onChange={(e) => handleChange(field.name, e.target.value)}
              className={`block w-full rounded-md border px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                error ? "border-red-500" : "border-gray-300"
              }`}
              placeholder={`Enter ${field.label.toLowerCase()}`}
              required={field.required}
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );

      case "select":
        return (
          <div key={field.name} className="mb-4">
            <label
              htmlFor={field.name}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              id={field.name}
              value={value}
              onChange={(e) => handleChange(field.name, e.target.value)}
              className={`block w-full rounded-md border px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                error ? "border-red-500" : "border-gray-300"
              }`}
              required={field.required}
            >
              <option value="">Select {field.label.toLowerCase()}</option>
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );

      case "brand-search":
        return (
          <div key={field.name} className="mb-4">
            <BrandSearchInput
              value={value ? Number(value) : null}
              brandName={formValues.kitebrandname || ""}
              onChange={handleBrandChange}
              required={field.required}
              error={error}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="text-gray-900">{title}</DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          {fields.map(renderField)}

          {apiError && <p className="mt-2 text-sm text-red-600">{apiError}</p>}
        </div>

        <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
          <button
            type="button"
            className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
            onClick={handleSubmit}
          >
            Save
          </button>
          <button
            type="button"
            className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default GearItemFormDialog;
